import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN || "https://a6a86bdb6922979bff34d13189aed58d@o4511412908261376.ingest.de.sentry.io/4511412922155088";

Sentry.init({
  dsn,
  environment: import.meta.env.MODE,
  // We can use import.meta.env.VITE_APP_VERSION if defined, otherwise fallback
  release: import.meta.env.VITE_APP_VERSION || "portfolio@1.0.0",

  sendDefaultPii: true,

  // Suppress non-actionable errors that are already handled at the app level:
  // - Sentry's own verification test signal
  // - Dynamic-import chunk failures (handled by page-reload in main.tsx)
  // - Browser-extension DOM mutations that conflict with React (patched in main.tsx)
  ignoreErrors: [
    "Sentry Verification Signal",
    "Failed to fetch dynamically imported module",
    "Importing a module script failed",
    "error loading dynamically imported module",
    /Failed to execute 'removeChild' on 'Node'/,
    /Failed to execute 'insertBefore' on 'Node'/,
    // Common browser extension / network noise
    "ResizeObserver loop",
    "ResizeObserver loop completed with undelivered notifications",
    // Cloudflare Zaraz telemetry errors (PORTFOLIO-6) — third-party infra,
    // thrown by /cdn-cgi/zaraz/s.js when their /cdn-cgi/zaraz/t endpoint 500s
    /Response not ok/,
    // Sandboxed contexts lacking allow-same-origin (PORTFOLIO-G)
    "SecurityError: Failed to read the 'localStorage' property from 'Window': The document is sandboxed and lacks the 'allow-same-origin' flag.",
    /Failed to read the 'localStorage' property from 'Window'/,
    // Android In-App Browser (Instagram/Facebook) bridge errors on unload (PORTFOLIO-H)
    "Error invoking enableButtonsClickedMetaDataLogging: Java object is gone",
    /enableButtonsClickedMetaDataLogging/,
    /Java object is gone/,
  ],

  // Suppress errors originating from third-party scripts we don't control
  denyUrls: [
    /\/cdn-cgi\//,
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^iabjs:\/\//i, // Android In-App Browser injected performance logging
  ],

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      matchRoutes,
      createRoutesFromChildren,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions. Adjust in production.
  tracePropagationTargets: ["localhost", /^https:\/\/mndl-27-portfolio\.netlify\.app/, /^https:\/\/protik\.eu\.org/],

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%.
  replaysOnErrorSampleRate: 1.0, // 100% when sampling sessions where errors occur.

  enableLogs: true,
});
